import { useCallback, useEffect, useRef } from "react";

/**
 * Sign out after a period of inactivity — measured across EVERY TAB, not
 * per tab (MPPI-00846).
 *
 * The first version listened only to its own window's events, so a second
 * admin tab left open in the background fired after an hour of not being
 * touched. That would be harmless if the timeout only affected that tab,
 * and it does not: `logout()` calls Cognito's `signOut()`, which clears
 * localStorage for the whole ORIGIN. So an idle background tab silently
 * destroyed the session of the tab the person was actively working in.
 *
 * The tab in front of them kept working — it holds its token in memory —
 * so nothing looked wrong until they opened a NEW tab, typically by
 * clicking a property link in the daily digest, and landed on /login.
 * That is the operator's exact report, twice.
 *
 * Activity is therefore recorded in localStorage, which every tab on the
 * origin shares. Any tab's activity resets the clock for all of them, and
 * the sign-out only fires when the whole browser has genuinely been idle
 * for the full period — which is what an inactivity timeout is for.
 *
 * The write is throttled: mousemove fires continuously, and a
 * localStorage write per mouse event would be a storm of disk I/O and of
 * `storage` events in every other tab.
 */
const ACTIVITY_KEY = "mppi:admin:last-activity";
const WRITE_THROTTLE_MS = 5_000;

function readLastActivity(): number {
  try {
    const raw = window.localStorage.getItem(ACTIVITY_KEY);
    const value = raw ? Number(raw) : NaN;
    return Number.isFinite(value) ? value : 0;
  } catch {
    // Private mode, blocked storage, quota — fall back to "no shared
    // record", which degrades to the old per-tab behaviour rather than
    // throwing on every mouse move.
    return 0;
  }
}

function writeLastActivity(at: number): void {
  try {
    window.localStorage.setItem(ACTIVITY_KEY, String(at));
  } catch {
    // Same reasoning as above: failing to record activity must never
    // break the page.
  }
}

export function useInactivityTimeout(timeoutMs: number, onTimeout: () => void) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastWriteRef = useRef(0);
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;

  /**
   * Schedule the check for when THIS browser would next become idle,
   * based on the shared record rather than on this tab's own last event.
   * If another tab has been active since, the wait is simply longer.
   */
  const schedule = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const last = readLastActivity() || Date.now();
    const elapsed = Date.now() - last;
    const remaining = timeoutMs - elapsed;

    if (remaining <= 0) {
      onTimeoutRef.current();
      return;
    }
    timerRef.current = setTimeout(() => {
      // Re-read on wake rather than trusting the delay: another tab may
      // have been busy the whole time this one slept, and a background
      // tab's timer can fire late after the machine suspends.
      const stillIdle = Date.now() - (readLastActivity() || 0) >= timeoutMs;
      if (stillIdle) onTimeoutRef.current();
      else schedule();
    }, remaining + 250);
  }, [timeoutMs]);

  const noteActivity = useCallback(() => {
    const now = Date.now();
    if (now - lastWriteRef.current >= WRITE_THROTTLE_MS) {
      lastWriteRef.current = now;
      writeLastActivity(now);
    }
    schedule();
  }, [schedule]);

  useEffect(() => {
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach((event) => window.addEventListener(event, noteActivity));

    // Another tab reporting activity must push this tab's deadline out
    // too, or the quietest tab still decides when everyone is signed out.
    const onStorage = (e: StorageEvent) => {
      if (e.key === ACTIVITY_KEY) schedule();
    };
    window.addEventListener("storage", onStorage);

    // Seed the shared record on mount so the first load of a fresh tab
    // counts as activity; without it a tab opened into an idle browser
    // would sign out immediately.
    writeLastActivity(Date.now());
    lastWriteRef.current = Date.now();
    schedule();

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      events.forEach((event) => window.removeEventListener(event, noteActivity));
      window.removeEventListener("storage", onStorage);
    };
  }, [noteActivity, schedule]);
}
